import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { translate } from '~l10n/client';

import { SmallButton } from '~shared/components/buttons/SmallButton';
import { SocrativeLogo } from '~shared/components/SocrativeLogo';
import { Spinner } from '~shared/components/Spinner';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import {
  RouterProvider,
  getTeacherRouter,
  useSocrativeRouter,
} from '~teacher/context/RouterContext';
import {
  useSendVerificationEmail,
  useVerifyEmail,
} from '~teacher/hook/account';
import { useVerificationData } from '~teacher/hook/account/useVerificationData';
import { useLogout } from '~teacher/hook/auth/useLogout';
import { queryClient } from '~teacher/queryClient';

import { isAxiosError } from '../../api/isAxiosError';

const teacherModel = require('../../../../../shared/models/TeacherModel');

const VERIFICATION_CODE_EXPIRATION_IN_MINUTES = 15;

export function VerifyEmailPage() {
  const verificationStatusQuery = useVerificationData({
    select: (data) => data.newEmail ?? data.email,
  });

  if (!verificationStatusQuery?.data) {
    return <VerifyEmailPageLoading />;
  }

  return <VerifyEmailPageData email={verificationStatusQuery.data} />;
}

function VerifyEmailPageLoading() {
  return (
    <div className="grid place-items-center">
      <div className="flex flex-col items-center justify-center min-h-screen gap-32 max-w-xl font-sans">
        <SocrativeLogo />
        <Spinner />
      </div>
    </div>
  );
}

function VerifyEmailPageData({ email }: { email: string }) {
  const teacherRouter = useSocrativeRouter();
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  const sendVerificationCode = useSendVerificationEmail();
  const submitVerificationCode = useVerifyEmail();
  const logoutMutation = useLogout();

  const codeRef = useRef<HTMLInputElement>();

  return (
    <div className="grid place-items-center">
      <div className="flex flex-col items-center justify-center min-h-screen gap-8 max-w-3xl font-sans">
        <SocrativeLogo />

        <span className="text-md font-display leading-loose  font-500 leading-normal text-blue-900">
          {translate('Verify Email')}
        </span>

        <div className="flex flex-col items-center">
          <p className="text-base leading-loose text-blue-900 text-center leading-normal">
            {translate('Verification is required to secure your account.')}
          </p>

          <p className="text-base leading-loose  text-blue-900 text-center leading-normal">
            {translate('We’ve sent a one time verification code to:')}
          </p>

          <p className="text-base leading-loose  font-700 text-blue-900">
            {email}
          </p>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.currentTarget.reportValidity();

            if (event.currentTarget.checkValidity()) {
              const code = codeRef.current.value;
              setIsVerifyingCode(true);
              submitVerificationCode.mutate(
                { code },
                {
                  onSuccess: () => {
                    teacherModel.setNewEmail(email);
                    toast.positive(
                      translate(
                        'Your email address has been successfully verified.'
                      )
                    );

                    teacherRouter.routeToLaunch();
                  },
                  onError: () => {
                    setIsVerifyingCode(false);
                    codeRef.current.setCustomValidity(
                      translate(
                        'Invalid or expired code. Try again or resend code.'
                      )
                    );
                    codeRef.current.reportValidity();
                  },
                }
              );
            }
          }}
          className="flex flex-col items-center gap-3"
        >
          <label className="flex flex-col leading-loose  items-center text-base text-blue-800 gap-3">
            {translate('Enter Verification Code')}
            <TextInput
              ref={codeRef}
              onInput={(event) => event.currentTarget.setCustomValidity('')}
              onInvalid={(event) => {
                const input = event.currentTarget;

                if (input.validity.customError) return; // Respect existing custom errors

                if (
                  input.validity.valueMissing ||
                  input.validity.tooShort ||
                  input.validity.tooLong
                ) {
                  input.setCustomValidity(translate('Invalid Code.'));
                }
              }}
              className="text-center"
              required
              minLength={'6' as any}
              maxLength={'6' as any}
              type="text"
            />
          </label>

          <SmallButton
            appearance="primary"
            isLoading={isVerifyingCode}
            type="submit"
          >
            {translate('Submit')}
          </SmallButton>
        </form>

        <p className="text-base leading-loose  text-blue-900 text-center leading-normal">
          {translate(
            'If you don’t see it, make sure you entered the correct email address and check your spam folder.'
          )}
        </p>

        <p className="text-base leading-loose  text-blue-900">
          {translate('Verification codes expire after {0}.').format(
            translate('{0} minutes').format(
              VERIFICATION_CODE_EXPIRATION_IN_MINUTES
            )
          )}
        </p>

        <div className="flex items-center h-6">
          <span className="text-base leading-loose  text-blue-900">
            {translate("Didn't receive it?")}
          </span>
          <SmallButton
            appearance="text"
            onClick={() => {
              if (isResendingVerification) return;
              setIsResendingVerification(true);
              setTimeout(() => setIsResendingVerification(false), 3000);
              sendVerificationCode.mutate(null, {
                onSuccess: () => {
                  toast.positive(translate('Sent'));
                },
                onError: (error: unknown) => {
                  if (isAxiosError(error)) {
                    if (error.response.status === 429) {
                      toast(
                        translate(
                          'Too many requests. Please wait a while and try again later.'
                        )
                      );
                    }
                  }
                  toast.critical(
                    translate(
                      'An error occurred. Please check your connection and try again.'
                    )
                  );
                },
              });
            }}
          >
            {translate('Resend Code')}
          </SmallButton>
        </div>
        <div className="h-6">
          <SmallButton
            appearance="text"
            onClick={() => {
              logoutMutation.mutate(null, {
                onSuccess: () => {
                  window.location.href = '/login/teacher/';
                },
                onError: (error: unknown) => {
                  if (__DEV__) {
                    console.error(error);
                  }
                  toast.critical(
                    translate(
                      'An unknown error occurred. Please try again later.'
                    )
                  );
                },
              });
            }}
          >
            {translate('Back to Login')}
          </SmallButton>
        </div>
      </div>
    </div>
  );
}

export default function doEmailVerifyPage() {
  const mainContentDiv = document.getElementById('main-content');
  if (!mainContentDiv) {
    throw new Error('Main content portal not found');
  }
  const teacherRouter = getTeacherRouter();

  ReactDOM.render(
    <RouterProvider router={teacherRouter}>
      <QueryClientProvider client={queryClient}>
        <VerifyEmailPage />
        <ReactQueryDevtools />
      </QueryClientProvider>
    </RouterProvider>,
    mainContentDiv
  );
}
